/**
 * Timezone helpers for the analytics surface.
 *
 * The whole admin analytics dashboard is anchored on America/New_York
 * (Eastern). This file is the single source of truth for that decision on
 * the frontend — backend mirrors it via `services.analytics.query_service.ANALYTICS_TZ`.
 *
 * Why explicit ET instead of "user's browser TZ"?
 * Founder dashboard semantics. We want every viewer to see the same numbers
 * for the same day-buckets, regardless of where the founder happens to be
 * looking from. Coach Cedar's operating timezone is Eastern, so that's the
 * lens.
 */

export const ANALYTICS_TZ = 'America/New_York';

/**
 * Today's date in the analytics anchor timezone (Eastern), as a YYYY-MM-DD
 * string. Use this anywhere you'd otherwise use
 * `new Date().toISOString().slice(0, 10)` (which is UTC, off by 4-5 hours).
 *
 * Implementation: en-CA locale formats Date as YYYY-MM-DD, which matches
 * the ISO-date strings returned by the backend.
 */
export function todayInAnalyticsTz(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: ANALYTICS_TZ });
}

/**
 * Format a YYYY-MM-DD string for axis labels. If the iso matches today (in
 * the analytics TZ), returns "Today (live)" so partial-day rollup data is
 * visually distinct from completed days.
 */
export function formatAnalyticsDayShort(iso: string, todayIso?: string): string {
  if (todayIso && iso === todayIso) return 'Today (live)';
  // The Date constructor parses 'YYYY-MM-DD' as UTC midnight, which can shift
  // the displayed day by one in browsers east of UTC. Append T12:00:00 to
  // avoid that.
  const d = new Date(iso + 'T12:00:00');
  if (isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: ANALYTICS_TZ,
  });
}
